/**
 * Platform capability catalog helpers (P0C).
 * Strict fetch parsing, filter/stats pure functions, safe error normalization.
 * No DTO redefinition — reuses @dosia/shared contract parsers only.
 */

import {
  parseCapabilityCatalogDetailResponseV1,
  parseCapabilityCatalogListResponseV1,
  type CapabilityAssetKindV1,
  type CapabilityCatalogItemV1,
  type CapabilityCatalogListV1,
  type CapabilityCatalogSourceSummaryV1,
  type CapabilityDeliveryModeV1,
  type CapabilityReadinessStatusV1,
} from "@dosia/shared";
import { ApiError } from "./api-types.js";

export type CatalogRegistrationFilter = "all" | "observed" | "registered";
export type CatalogDeliveryFilter = "all" | "unregistered" | CapabilityDeliveryModeV1;
export type CatalogHealthFilter = "all" | CapabilityReadinessStatusV1;
export type CatalogAssetKindFilter = "all" | CapabilityAssetKindV1;

export interface CapabilityCatalogFilters {
  search: string;
  assetKind: CatalogAssetKindFilter;
  /** "all" or a value from item.sourceCoverage.sources only (not provenance sourceKind). */
  source: string;
  delivery: CatalogDeliveryFilter;
  catalogState: CatalogRegistrationFilter;
  health: CatalogHealthFilter;
}

export interface CapabilityCatalogStats {
  total: number;
  skillCount: number;
  agentCount: number;
  workflowCount: number;
  jobTemplateCount: number;
  sourceHealth: {
    ready: number;
    blocked: number;
    unknown: number;
  };
}

export type CatalogLoadErrorKind = "network" | "http" | "contract";

export interface CatalogLoadError {
  kind: CatalogLoadErrorKind;
  /** Safe, user-facing message (never raw body / stack / token). */
  message: string;
  status?: number;
}

/**
 * Parse list envelope; throw on null/undefined/204 body or contract failure.
 * Caller maps any throw to UI contract/network/http state.
 */
export function parseCatalogListResponse(raw: unknown): CapabilityCatalogListV1 {
  if (raw == null) {
    throw new Error("empty catalog list response");
  }
  return parseCapabilityCatalogListResponseV1(raw).data;
}

/**
 * Parse detail envelope; throw on empty body, contract failure, or id mismatch.
 * @param expectedCatalogItemId when set, response item.catalogItemId must equal it.
 */
export function parseCatalogDetailResponse(
  raw: unknown,
  expectedCatalogItemId?: string,
): CapabilityCatalogItemV1 {
  if (raw == null) {
    throw new Error("empty catalog detail response");
  }
  const item = parseCapabilityCatalogDetailResponseV1(raw).data.item;
  if (
    expectedCatalogItemId != null &&
    item.catalogItemId !== expectedCatalogItemId
  ) {
    throw new Error("catalog detail id mismatch");
  }
  return item;
}

/**
 * Map transport/HTTP failures to safe UI errors.
 * Contract failures are handled by the caller around shared parsers — never map
 * arbitrary Error (e.g. fetch TypeError) to contract.
 */
export function normalizeCatalogError(err: unknown, fallbackMessage: string): CatalogLoadError {
  if (err instanceof ApiError) {
    if (err.status === 0 || err.code === "client-error") {
      return { kind: "network", message: fallbackMessage, status: err.status };
    }
    return {
      kind: "http",
      message: fallbackMessage,
      status: err.status,
    };
  }
  return { kind: "network", message: fallbackMessage };
}

export function computeCatalogStats(
  items: readonly CapabilityCatalogItemV1[],
  sourceSummaries: readonly CapabilityCatalogSourceSummaryV1[],
): CapabilityCatalogStats {
  let skillCount = 0;
  let agentCount = 0;
  let workflowCount = 0;
  let jobTemplateCount = 0;
  for (const item of items) {
    switch (item.assetKind) {
      case "skill":
        skillCount++;
        break;
      case "agent":
        agentCount++;
        break;
      case "workflow":
        workflowCount++;
        break;
      case "job_template":
        jobTemplateCount++;
        break;
    }
  }
  let ready = 0;
  let blocked = 0;
  let unknown = 0;
  for (const s of sourceSummaries) {
    if (s.readiness.status === "ready") ready++;
    else if (s.readiness.status === "blocked") blocked++;
    else unknown++;
  }
  return {
    total: items.length,
    skillCount,
    agentCount,
    workflowCount,
    jobTemplateCount,
    sourceHealth: { ready, blocked, unknown },
  };
}

/** Dynamic source filter options — only real coverage sources, not provenance sourceKind. */
export function collectSourceOptions(items: readonly CapabilityCatalogItemV1[]): string[] {
  const set = new Set<string>();
  for (const item of items) {
    for (const s of item.sourceCoverage.sources) {
      const t = s.trim();
      if (t) set.add(t);
    }
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

function matchesSearch(item: CapabilityCatalogItemV1, q: string): boolean {
  if (!q) return true;
  const hay = [
    item.name,
    item.description,
    item.source.sourceKind,
    ...item.sourceCoverage.sources,
  ]
    .join("\n")
    .toLowerCase();
  return hay.includes(q);
}

function matchesDelivery(item: CapabilityCatalogItemV1, delivery: CatalogDeliveryFilter): boolean {
  if (delivery === "all") return true;
  if (delivery === "unregistered") return item.catalogState === "observed";
  if (item.catalogState !== "registered") return false;
  return item.deliveryMode === delivery;
}

/** Source filter matches coverage sources only (not internal provenance sourceKind). */
function matchesSource(item: CapabilityCatalogItemV1, source: string): boolean {
  if (!source || source === "all") return true;
  return item.sourceCoverage.sources.includes(source);
}

export function filterCapabilityItems(
  items: readonly CapabilityCatalogItemV1[],
  filters: CapabilityCatalogFilters,
): CapabilityCatalogItemV1[] {
  const q = filters.search.trim().toLowerCase();
  return items.filter((item) => {
    if (filters.assetKind !== "all" && item.assetKind !== filters.assetKind) return false;
    if (filters.catalogState !== "all" && item.catalogState !== filters.catalogState) return false;
    if (!matchesDelivery(item, filters.delivery)) return false;
    if (!matchesSource(item, filters.source)) return false;
    if (filters.health !== "all" && item.sourceCoverage.readiness.status !== filters.health) {
      return false;
    }
    if (!matchesSearch(item, q)) return false;
    return true;
  });
}

export const DEFAULT_CAPABILITY_FILTERS: CapabilityCatalogFilters = {
  search: "",
  assetKind: "all",
  source: "all",
  delivery: "all",
  catalogState: "all",
  health: "all",
};

export function isFilterActive(filters: CapabilityCatalogFilters): boolean {
  return (
    filters.search.trim() !== "" ||
    filters.assetKind !== "all" ||
    filters.source !== "all" ||
    filters.delivery !== "all" ||
    filters.catalogState !== "all" ||
    filters.health !== "all"
  );
}
